import { ChangeDetectorRef, Component, ElementRef, Inject, ViewChild } from "@angular/core";
import { NZ_MODAL_DATA, NzModalRef } from "ng-zorro-antd/modal";
import { Subject, takeUntil } from "rxjs";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { VideoRecordingService } from "../../../../../../../../core/src/lib/recording/video-recording.service";
import {getSeekableBlob} from "recordrtc";

@Component({
  selector: "mh-message-record-video-dialog",
  templateUrl: "./mh-message-record-video-dialog.component.html",
  styleUrls: ["./mh-message-record-video-dialog.component.less"],
})
export class MhMessageRecordVideoDialogComponent {
  @ViewChild('videoElement') videoElement!: ElementRef;

  private readonly destroy$ = new Subject<void>();
  isRecording = false;
  recordingLoading = false;
  recordedTime = '';
  cameraUnavailable = false;
  blobUrl?: SafeUrl | null;
  private recordedVideoUrl?: string;
  private blob?: Blob;
  private mediaName = '';
  previewVideoElement: any;
  fileList: FileList | null | undefined;

  constructor(
    @Inject(NZ_MODAL_DATA) public modalData: any,
    private modal: NzModalRef,
    private videoRecordingService: VideoRecordingService,
    private ref: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
  ) {
    this.setupVideoRecordingSubscriptions();
  }

  private setupVideoRecordingSubscriptions() {
    this.videoRecordingService.recordingFailed().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleRecordingFailure();
    });

    this.videoRecordingService.getRecordedTime().pipe(takeUntil(this.destroy$)).subscribe((time) => {
      this.handleRecordedTime(time);
    });

    this.videoRecordingService.getRecordedBlob().pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.handleRecordedBlob(data, () => this.handleVideoUpload());
    });
  }

  private handleRecordingFailure() {
    this.isRecording = false;
    this.recordingLoading = false;
    this.cameraUnavailable = true;
    this.ref.detectChanges();
  }

  private handleRecordedTime(time: any) {
    this.recordedTime = time;
    this.ref.detectChanges();
  }

  private handleRecordedBlob(data: any, handleUpload: () => void) {
    if (data.blob) {
      this.blob = data.blob;
      this.mediaName = data.title;
      this.revokeRecordedVideoUrl();
      this.recordedVideoUrl = URL.createObjectURL(data.blob);
      this.blobUrl = this.sanitizer.bypassSecurityTrustUrl(this.recordedVideoUrl);
      this.ref.detectChanges();
      this.playRecordedVideo();
      handleUpload();
    }
  }

  ngAfterViewInit() {
    this.previewVideoElement = this.videoElement.nativeElement;
  }

  startVideoRecording() {
    if (!this.isRecording && this.previewVideoElement) {
      this.cameraUnavailable = false;
      this.recordingLoading = true;
      this.isRecording = true;
      this.previewVideoElement.controls = false;
      this.previewVideoElement.muted = true;
      this.videoRecordingService.startRecording().then(stream => {
        this.previewVideoElement.srcObject = stream;
        this.previewVideoElement.play();
        this.recordingLoading = false;
      })
        .catch((err) => {
          console.log(err.name + ": " + err.message);
          this.handleRecordingFailure();
        });
    }
  }

  abortVideoRecording() {
    if (this.isRecording) {
      this.isRecording = false;
      this.videoRecordingService.abortRecording();
    }
  }

  stopVideoRecording() {
    if (this.isRecording) {
      this.videoRecordingService.stopRecording();
      this.isRecording = false;
      this.previewVideoElement.controls = true;
    }
  }

  handleVideoUpload(): void {
    if (this.blob) {
      let self = this;
        getSeekableBlob(this.blob, function(seekableBlob) {
          const file = self.modifyFileMimeType(new File([seekableBlob], self.mediaName, { type:seekableBlob.type }));
          const dt = new DataTransfer();
          dt.items.add(file);
          self.fileList = dt.files;
        });
    }
  }

  retakeVideo(): void {
    this.blobUrl = null;
    this.revokeRecordedVideoUrl();
    this.previewVideoElement.srcObject = null;
    this.previewVideoElement.removeAttribute('src');
    this.previewVideoElement.load();
    this.startVideoRecording();
  };

  get recordingStatus(): string {
    if (this.cameraUnavailable) return this.translateService.instant("message.record-video.status.camera-blocked");
    if (this.isRecording) return this.translateService.instant("message.record-video.status.recording");
    if (this.blobUrl) return this.translateService.instant("message.record-video.status.ready");
    return this.translateService.instant("message.record-video.status.idle");
  }

  private playRecordedVideo(): void {
    setTimeout(() => {
      if (!this.previewVideoElement) return;

      this.previewVideoElement.srcObject = null;
      this.previewVideoElement.src = this.recordedVideoUrl || '';
      this.previewVideoElement.load();
      this.previewVideoElement.muted = true;
      this.previewVideoElement.play()
        .then(() => this.previewVideoElement.muted = false)
        .catch(() => this.previewVideoElement.muted = false);
    });
  }

  onCancel() {
    this.modal.close();
  }

  onSubmit() {
    return this.modal.triggerOk();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.abortVideoRecording();
    this.revokeRecordedVideoUrl();
  }

  private revokeRecordedVideoUrl(): void {
    if (this.recordedVideoUrl) {
      URL.revokeObjectURL(this.recordedVideoUrl);
      this.recordedVideoUrl = undefined;
    }
  }

  private modifyFileMimeType(originalFile: File): File {
    const codecsMatch = originalFile.type.match(/codecs=([^;]*)/);
    const codecs = codecsMatch ? codecsMatch[1] : "";

    if (codecs) {
      const modifiedMimeType = originalFile.type.replace(codecs, `"${codecs}"`);
      const modifiedFile = new File([originalFile], originalFile.name, { type: modifiedMimeType, lastModified: originalFile.lastModified });
      return modifiedFile;
    }

    return originalFile;
  }
}
